import { PACKAGING_ITEMS_RATING } from '@app/ccf/company-report/utils';
import ReportContent from '@app/components/reports/components/report-content';
import { EmissionsCategoryEnum } from '@shared/api/types';
import { itemsByIntensityChartConfig } from '@shared/components/chart/configs';
import SimplePieChart from '@shared/components/chart/simple-pie-chart';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { Card } from '@shared/components/ui/card';
import { cn, roundNumberToNDecimalPlaces } from '@shared/lib/utils';
import { useCCFReportResult } from '../../ccf-report-result-context';

function HighestEmissionsItemsCard({
  item,
  rating,
  totalPurchased,
  totalEmissions,
  pctOfEmissions,
}: {
  item: string;
  rating?: EmissionsCategoryEnum;
  totalPurchased: number;
  totalEmissions: number;
  pctOfEmissions: number;
}) {
  return (
    <Card className="grid grid-cols-11 items-center gap-4 border-teal-400 p-3">
      <Text
        includeHover
        className="col-span-3 !line-clamp-2 !text-ellipsis font-heading font-medium"
      >
        {item}
      </Text>
      <div className="col-span-2 mx-auto">
        <div
          className={cn('size-4 shrink-0 rounded-full mt-0.5 ')}
          style={{
            backgroundColor: PACKAGING_ITEMS_RATING[rating].color,
          }}
        ></div>
      </div>
      <Text className="col-span-2 text-right">
        {roundNumberToNDecimalPlaces(totalPurchased, 2, false)}
      </Text>
      <Text className="col-span-2 text-right">
        {roundNumberToNDecimalPlaces(totalEmissions, 2, false)}
      </Text>
      <Text className="col-span-2 text-right">
        {roundNumberToNDecimalPlaces(pctOfEmissions, 2, false)}%
      </Text>
    </Card>
  );
}

export default function PackagingItems() {
  const {
    result: { packaging },
  } = useCCFReportResult();
  const data = {
    highest_emissions_items: packaging?.highestEmissionPackaging.map((item) => {
      return {
        emissionsTonnes: item.emissionsTonnes,
        name: item.name,
        pctOfCompanyEmissions: item.pctOfCompanyEmissions,
        weightKg: item.weightKg / 1000,
        rating: item.rating as EmissionsCategoryEnum,
      };
    }),

    items_by_carbon_intensity: packaging?.itemsByCarbonIntensityCategory,
    items_assessed: packaging?.itemsByCarbonIntensityCategory.reduce(
      (acc, category) => acc + category.numberOfItems,
      0
    ),
  };

  const chartData = data.items_by_carbon_intensity?.map((item) => {
    const category =
      item.carbonIntensityCategory as keyof typeof PACKAGING_ITEMS_RATING;
    return {
      category,
      items: item.numberOfItems,
      fill: PACKAGING_ITEMS_RATING[category].color,
    };
  });

  return (
    <ReportContent>
      <>
        <Text variant="reportBody">
          Similar to food and drink, it is important to consider both the total
          emissions from the packaging items you purchase and their carbon
          intensity - the amount of CO₂e emitted per kilogram of packaging.
          <br />
          <br />
          Whilst the total emissions shows the overall impact on your carbon
          footprint, the carbon intensity helps you identify items where you
          could look for a lower-intensity item to reduce emissions.
        </Text>
        <Text variant="h4">Items by carbon intensity</Text>
        <div className="flex items-center">
          <SimplePieChart
            dataKey="items"
            nameKey="category"
            chartConfig={itemsByIntensityChartConfig}
            chartData={chartData!.reverse()}
            innerRadius={60}
            centerLabel={{
              value: data.items_assessed!,
              label: 'Items',
            }}
            className="size-64"
          />
          <div className="flex h-full flex-1 flex-col items-start justify-start gap-y-3">
            {(
              ['Low', 'Medium', 'High'] as Array<
                keyof typeof PACKAGING_ITEMS_RATING
              >
            ).map((rating, idx) => {
              return (
                <div key={idx} className="flex w-full items-center gap-2">
                  <div className="flex gap-2">
                    <div
                      className={cn('size-4 shrink-0 rounded-sm mt-0.5 ')}
                      style={{
                        backgroundColor: PACKAGING_ITEMS_RATING[rating].color,
                      }}
                    ></div>
                    <Text className="mt-0">{rating}</Text>
                  </div>

                  <div className="flex items-baseline">
                    <Text variant="unit" className="text-nowrap">
                      {PACKAGING_ITEMS_RATING[rating].threshold}
                    </Text>
                    <Unit variant="kgCO2e/kg" className="ml-1" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
      <>
        <Text variant="h3">Items by highest total emissions</Text>
        <div className="space-y-3">
          <div className="grid grid-cols-11 gap-x-4">
            <Text className="col-span-3">Packaging item</Text>
            <Text className="col-span-2 mx-auto">Rating</Text>
            <Text className="col-span-2 text-center">
              Total purchased <Unit variant="kg" />
            </Text>
            <Text className="col-span-2 text-center">
              Total emissions <Unit variant="tCO2e" />
            </Text>
            <Text className="col-span-2 text-center">% of emissions</Text>
          </div>
          {data.highest_emissions_items?.map((item, idx) => {
            return (
              idx < 7 && (
                <HighestEmissionsItemsCard
                  key={idx}
                  item={item.name}
                  rating={item.rating}
                  totalPurchased={item.weightKg}
                  totalEmissions={item.emissionsTonnes}
                  pctOfEmissions={item.pctOfCompanyEmissions}
                />
              )
            );
          })}
        </div>
      </>
    </ReportContent>
  );
}
